import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { adminAPI, bonusesAPI, libraryAPI, ordersAPI, partnerAPI } from "@/services/api";
import { ADMIN_RANKS, hasExplicitAdminRank } from "@/utils/adminRanks";
import { wakeBackend } from "@/services/backendWake";
import MobileBottomNav from "@/components/app/MobileBottomNav";
import { formatCurrency } from "@/utils/constants";
import articoLogo from "@/assets/artico-logo.png";
import {
  BookOpen,
  ClipboardList,
  FileText,
  Library,
  MessageCircle,
  Shield,
  ShoppingBag,
  Sparkles,
  Users,
  Wallet,
  Zap,
  UserCircle2,
} from "lucide-react";

function getCurrentMonthKey() {
  return new Date().toISOString().slice(0, 7);
}

function RoleAction({ href, icon: Icon, title, description, accent = false }) {
  return (
    <Link
      to={href}
      className={`rounded-3xl border p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        accent ? "border-[#2A4B46] bg-[#2A4B46] text-white" : "border-[#E6E4E0] bg-white text-[#2A4B46]"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${accent ? "bg-white/15" : "bg-[#E7EFEC]"}`}>
          <Icon size={20} />
        </div>
        <span className="min-w-0">
          <p className="font-semibold">{title}</p>
          <p className={`mt-1 text-xs leading-5 ${accent ? "text-white/80" : "text-[#5C625F]"}`}>{description}</p>
        </span>
      </div>
    </Link>
  );
}

function MiniCover({ item, onOpen }) {
  const coverPath = item.thumbnail_path || item.cover_image_path;
  const coverUrl = coverPath ? getFileUrl(coverPath) : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="min-w-[150px] overflow-hidden rounded-3xl border border-[#E6E4E0] bg-white text-left shadow-sm"
    >
      <div className="aspect-[4/5] bg-[#263A36]">
        {coverUrl ? (
          <img src={coverUrl} alt={item.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-[#2A4B46] to-[#D4A373]">
            <BookOpen className="text-white" size={28} />
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="line-clamp-2 text-sm font-semibold leading-5 text-[#2A4B46] font-['Outfit']">{item.title}</p>
        <p className="mt-1 text-[11px] text-[#5C625F]">{item.author_name || "ARTICO"}</p>
      </div>
    </button>
  );
}

export default function MobileRoleHomePage({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activation, setActivation] = useState(null);
  const [bonusSummary, setBonusSummary] = useState(null);
  const [libraryItems, setLibraryItems] = useState([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    wakeBackend({ force: true, reason: `mobile-role-${user?.role || "guest"}` });
  }, [user?.role]);

  useEffect(() => {
    let alive = true;

    if (!user) {
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    setLoading(true);

    const load = async () => {
      try {
        if (user.role === "admin") {
          const [statsRes] = await Promise.allSettled([adminAPI.getStats()]);
          if (!alive) return;
          if (statsRes.status === "fulfilled") setStats(statsRes.value.data || null);
          return;
        }

        if (user.role === "partner") {
          const [activationRes, bonusRes, libraryRes] = await Promise.allSettled([
            partnerAPI.getActivationStatus(),
            bonusesAPI.getSummary({ month: getCurrentMonthKey() }),
            libraryAPI.list(),
          ]);
          if (!alive) return;
          if (activationRes.status === "fulfilled") setActivation(activationRes.value.data || null);
          if (bonusRes.status === "fulfilled") setBonusSummary(bonusRes.value.data || null);
          if (libraryRes.status === "fulfilled") {
            const items = Array.isArray(libraryRes.value.data?.items) ? libraryRes.value.data.items : [];
            setLibraryItems(items);
          }
          return;
        }

        const [ordersRes, libraryRes] = await Promise.allSettled([ordersAPI.list(), libraryAPI.list()]);
        if (!alive) return;
        if (ordersRes.status === "fulfilled") {
          const rows = Array.isArray(ordersRes.value.data) ? ordersRes.value.data : [];
          setOrdersCount(rows.length);
        }
        if (libraryRes.status === "fulfilled") {
          const items = Array.isArray(libraryRes.value.data?.items) ? libraryRes.value.data.items : [];
          setLibraryItems(items);
        }
      } catch (error) {
        console.error("Mobile role home load failed", error);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [user]);

  const roleTitle = useMemo(() => {
    if (user?.role === "admin") return "Espace admin mobile";
    if (user?.role === "partner") return "Espace partenaire mobile";
    if (user?.role === "buyer") return "Espace acheteur mobile";
    return "Espace mobile";
  }, [user?.role]);

  const roleDescription = useMemo(() => {
    if (user?.role === "admin") return "Pilotage rapide des validations, de la caisse et des partenaires.";
    if (user?.role === "partner") return "Votre bibliothèque, vos documents, votre coaching et vos bonus.";
    if (user?.role === "buyer") return "Votre bibliothèque personnelle, vos achats et le catalogue public.";
    return "Votre accès MMJ.";
  }, [user?.role]);

  const actions = useMemo(() => {
    if (user?.role === "admin") {
      const adminActions = [
        { href: "/admin/kyc", icon: FileText, title: "KYC", description: "Valider les dossiers partenaires", accent: true },
        { href: "/admin/users", icon: Users, title: "Partenaires", description: "Consulter et gérer les comptes" },
        { href: "/admin/activations", icon: Zap, title: "Activations", description: "Paiements et lots" },
        { href: "/admin/artico-cash", icon: Wallet, title: "Caisse", description: "Mouvements et clôtures" },
        { href: "/admin/products", icon: BookOpen, title: "Produits", description: "Valider et modifier les œuvres" },
        { href: "/admin/system", icon: Shield, title: "Système", description: "Surveillance et audits" },
      ];
      if (hasExplicitAdminRank(user, ADMIN_RANKS.CL)) {
        adminActions.unshift({ href: "/admin/catalogue-libre/submit", icon: BookOpen, title: "Catalogue libre", description: "Déposer une oeuvre gratuite avec ses droits", accent: true });
      }
      return adminActions;
    }
    if (user?.role === "partner") {
      return [
        { href: "/library", icon: Library, title: "Ma bibliothèque", description: "Lancer la lecture ou l'écoute", accent: true },
        { href: "/documents", icon: FileText, title: "Mes documents", description: "Contrat, KYC et archives" },
        { href: "/products/submit", icon: ShoppingBag, title: "Soumettre", description: "Ajouter un livre, audio ou vidéo" },
        { href: "/bonuses", icon: Sparkles, title: "Bonus", description: "Suivre vos gains du mois" },
        { href: "/team", icon: Users, title: "Mon équipe", description: "Suivi du coaching et de la lignée" },
        { href: "/verification", icon: Shield, title: "Vérification", description: "Etat du compte et du programme" },
      ];
    }
    return [
      { href: "/library", icon: Library, title: "Ma bibliothèque", description: "Ouvrir vos contenus" , accent: true },
        { href: "/public/catalogue", icon: ShoppingBag, title: "Catalogue", description: "Découvrir de nouvelles œuvres" },
      { href: "/orders", icon: ClipboardList, title: "Mes commandes", description: `Historique et suivi (${ordersCount})` },
      { href: "/messages", icon: MessageCircle, title: "Messagerie", description: "Parler au support ou à la communauté" },
      { href: "/profile", icon: UserCircle2, title: "Profil", description: "Vos informations et documents" },
    ];
  }, [ordersCount, user]);

  if (!user) {
    return (
      <main className="min-h-dvh bg-[#FCFBF8] px-4 py-8">
        <Card className="mx-auto max-w-md border-[#E6E4E0]">
          <CardContent className="space-y-4 py-8 text-center">
            <img src={articoLogo} alt="MMJ" className="mx-auto h-14 w-14 rounded-2xl object-contain" />
            <h1 className="text-xl font-bold text-[#2A4B46] font-['Outfit']">Accès mobile MMJ</h1>
            <p className="text-sm leading-6 text-[#5C625F]">Connectez-vous pour retrouver votre espace personnel et vos contenus.</p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button className="rounded-full bg-[#2A4B46] text-white" onClick={() => navigate("/login")}>Connexion</Button>
              <Button variant="outline" className="rounded-full border-[#2A4B46] text-[#2A4B46]" onClick={() => navigate("/register")}>Inscription</Button>
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-dvh bg-[#FCFBF8] px-4 py-4 text-[#1A1D1C]">
      <section className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={articoLogo} alt="MMJ" className="h-12 w-12 rounded-2xl object-contain" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D4A373]">{roleTitle}</p>
              <p className="text-xs text-[#5C625F]">{user.full_name}</p>
            </div>
          </div>
          <Badge className="rounded-full bg-[#E7EFEC] text-[#2A4B46]">{user.role}</Badge>
        </div>

        <Card className="mt-4 border-0 bg-gradient-to-br from-[#2A4B46] via-[#315f58] to-[#D4A373] text-white shadow-xl">
          <CardContent className="space-y-3 p-5">
            <h1 className="text-2xl font-bold leading-tight font-['Outfit']">{roleTitle}</h1>
            <p className="text-sm leading-6 text-white/85">{roleDescription}</p>
            <div className="flex flex-wrap gap-2">
              <Button className="rounded-full bg-white text-[#2A4B46] hover:bg-white/90" onClick={() => navigate(user.role === "admin" ? "/dashboard" : user.role === "partner" ? "/library" : "/library")}>
                Ouvrir
              </Button>
              <Button variant="outline" className="rounded-full border-white/35 bg-white/10 text-white hover:bg-white/20" onClick={() => navigate("/public/catalogue")}>
                Catalogue
              </Button>
            </div>
          </CardContent>
        </Card>

        {(user.role === "partner" || user.role === "buyer") && (
          <section className="mt-4 space-y-3">
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D4A373]">Bibliothèque</p>
                <h2 className="mt-1 text-lg font-bold text-[#2A4B46] font-['Outfit']">Votre espace de lecture</h2>
              </div>
              <Button variant="ghost" className="text-[#2A4B46]" onClick={() => navigate("/library")}>Ouvrir</Button>
            </div>
            {loading ? (
              <Card className="border-[#E6E4E0]">
                <CardContent className="py-8 text-center text-sm text-[#5C625F]">Chargement...</CardContent>
              </Card>
            ) : libraryItems.length ? (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {libraryItems.slice(0, 4).map((item) => (
                  <MiniCover key={item.id} item={item} onOpen={() => navigate("/library")} />
                ))}
              </div>
            ) : (
              <Card className="border-[#E6E4E0]">
                <CardContent className="space-y-3 py-8 text-center">
                  <Library size={36} className="mx-auto text-[#2A4B46]" />
                  <p className="text-sm leading-6 text-[#5C625F]">Votre bibliothèque est encore vide. Le catalogue public est prêt à prendre le relais.</p>
                  <Button className="rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={() => navigate("/public/catalogue")}>Voir le catalogue</Button>
                </CardContent>
              </Card>
            )}
          </section>
        )}

        {user.role === "partner" && (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <InfoChip label="PAR" value={activation?.is_active_par ? "Actif" : "Inactif"} />
            <InfoChip label="PAE" value={activation?.is_active_pae ? "Actif" : "Inactif"} />
            <InfoChip label="Bonus mois" value={bonusSummary ? `${formatCurrency(bonusSummary.total || 0)}` : "0 MC"} />
          </div>
        )}

        {user.role === "buyer" && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <InfoChip label="Commandes" value={String(ordersCount)} />
            <InfoChip label="Livres" value={String(libraryItems.length)} />
          </div>
        )}

        {user.role === "admin" && stats && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <InfoChip label="Utilisateurs" value={String(stats.total_users || 0)} />
            <InfoChip label="Partenaires" value={String(stats.total_partners || 0)} />
            <InfoChip label="Produits" value={String(stats.total_products || 0)} />
            <InfoChip label="Commandes" value={String(stats.total_orders || 0)} />
          </div>
        )}

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {actions.map((action) => (
            <RoleAction key={action.href} {...action} />
          ))}
        </div>
      </section>
      <MobileBottomNav user={user} />
    </main>
  );
}

function InfoChip({ label, value }) {
  return (
    <Card className="border-[#E6E4E0] bg-white">
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#5C625F]">{label}</p>
        <p className="text-sm font-bold text-[#2A4B46]">{value}</p>
      </CardContent>
    </Card>
  );
}

function getFileUrl(path) {
  const token = localStorage.getItem("mmj_token");
  return `${window.location.origin.replace(/\/$/, "")}/api/files/${path}?auth=${token}`;
}
